import moment from 'moment-timezone';
import AppointmentUtils from './AppointmentUtils';
import CalendarUtils from './CalendarUtils';
import _ from 'lodash';
import PerformanceUtils from 'services/PerformanceUtils';
import { Appointment } from 'generated/api';

interface ChatRoom {
  id: string;
  patient_id?: string;
  staff_id?: string;
  archive_on?: boolean;
  creation_date?: number;
  [key: string]: any;
}

interface ChatBubble {
  isLiveChat?: boolean;
  type: string;
  id: string;
  appointment_id?: string;
  appointment_type?: string;
  patient_id?: string;
  booked_by?: string;
  patient_in_room?: boolean;
  appointment?: Appointment;
  description?: string;
  room_name?: string;
  room?: ChatRoom;
  chat: boolean;
  date?: number;
}

class ChatBubbleUtils {
  static getAllChatBubbles(
    chatRooms: ChatRoom[],
    activeAppointments: Record<string, Appointment>,
    inRoomPatientIds: string[],
  ): ChatBubble[] {
    let bubbles: ChatBubble[] = [];
    let today = moment().startOf('day');

    for (let appointmentId in activeAppointments) {
      if (activeAppointments.hasOwnProperty(appointmentId)) {
        let appointment = activeAppointments[appointmentId]!;
        let appointmentDate = moment.unix(appointment.date!);
        //hide future clinic appointments (tomorrow and onwards)

        let { type, status } = appointment;
        const TYPES = AppointmentUtils.getAppointmentTypes();
        const STATUS = AppointmentUtils.getAppointmentStatus();
        if (appointmentDate.isSame(today, 'd') || appointmentDate.isBefore(today)) {
          const patientInRoom = _.includes(inRoomPatientIds, appointment.patient_id);

          let shouldInclude = false;

          if (status === STATUS.checked_in) shouldInclude = true;

          if (
            status === STATUS.scheduled &&
            patientInRoom &&
            (appointment as any).linked?.length > 0
          ) {
            shouldInclude = true;
          }

          if (type === TYPES.home && appointment.date! > moment().add(1, 'hour').unix()) {
            shouldInclude = true;
          }

          if (shouldInclude) {
            let bubble = this.chatBubbleFromAppointment(appointment, patientInRoom);
            bubbles.push(bubble);
          }
        }
      }
    }

    function appointmentChatMatch(existingBubble: ChatBubble, bubble: ChatBubble): boolean {
      return (
        existingBubble.id === bubble.appointment_id ||
        (bubble.patient_id === existingBubble.patient_id &&
          existingBubble.chat === false &&
          (bubble.type === 'on-demand' || bubble.type === 'general'))
      );
    }

    for (let index in chatRooms) {
      let room = chatRooms[index]!;
      let bubble = ChatBubbleUtils.chatBubbleFromChatRoom(room);
      if (!bubble) continue;

      let existingAppointmentBubbles = bubbles.filter((existingBubble) => {
        return appointmentChatMatch(existingBubble, bubble!);
      });

      if (existingAppointmentBubbles.length === 0) {
        bubbles.push(bubble);
      } else {
        existingAppointmentBubbles.forEach((existingBubble) => {
          existingBubble.room_name = bubble!.id;
          existingBubble.chat = true;
          existingBubble.isLiveChat = true;
        });
      }
    }

    return PerformanceUtils.sortBy(bubbles, ['date'], false);
  }

  static chatBubbleFromChatRoom(room: ChatRoom): ChatBubble | null {
    if (!room.staff_id || room.archive_on) return null;
    return {
      isLiveChat: true,
      type: 'general',
      id: room.id,
      patient_id: room.patient_id,
      room,
      room_name: room.id,
      chat: true,
      date: room.creation_date,
    };
  }

  static chatBubbleFromAppointment(
    appointment: Appointment,
    patientInRoom: boolean = false,
  ): ChatBubble {
    let type = appointment.physician_id !== undefined ? 'appointment' : 'schedule-appointment';

    let description =
      AppointmentUtils.getAppointmentDescription(appointment) +
      ` (${appointment.status})` +
      ', ' +
      CalendarUtils.format(appointment.date!, 'Do MMMM YYYY h:mm A');

    return {
      id: appointment.id!,
      appointment_id: appointment.id,
      appointment_type: appointment.type,
      patient_id: appointment.patient_id,
      booked_by: (appointment as any).booked_by,
      patient_in_room: patientInRoom,
      appointment,
      description,
      type,
      chat: false,
      date: appointment.date,
    };
  }
}

export default ChatBubbleUtils;
